import { FormEvent, useEffect, useMemo, useState } from "react";
import { CalendarDays, CheckCircle2, Clock3 } from "lucide-react";
const root = location.hostname.endsWith(".taskroute.co.uk")
    ? "https://taskroute.co.uk"
    : "",
  slug = location.hostname.endsWith(".taskroute.co.uk")
    ? location.hostname.slice(0, -16)
    : localStorage.getItem("taskroute-tenant") || "northstar";
const call = async (path: string, init: RequestInit = {}) => {
  const r = await fetch(`${root}${path}`, {
      ...init,
      headers: { "x-tenant-slug": slug, ...(init.headers || {}) },
    }),
    x = await r.json().catch(() => ({}));
  if (!r.ok)
    throw new Error(
      String(x.error || "Unable to continue").replaceAll("_", " "),
    );
  return x;
};
const money = (p: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    p / 100,
  );
function ManageBooking({ reference, token, booking, onCancel }: any) {
  const [date, setDate] = useState(
      new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    ),
    [slots, setSlots] = useState<any[]>([]),
    [error, setError] = useState(""),
    [busy, setBusy] = useState(false);
  async function find() {
    setError("");
    try {
      const q = new URLSearchParams({ serviceId: booking.service_id, date });
      setSlots((await call(`/api/public/appointments/slots?${q}`)).slots);
    } catch (e: any) {
      setError(e.message);
    }
  }
  async function move(slot: any) {
    if (
      !confirm(
        `Move this appointment to ${new Date(slot.startsAt).toLocaleString("en-GB")} with ${slot.staffName}?`,
      )
    )
      return;
    setBusy(true);
    try {
      await call(
        `/api/public/appointments/manage/${encodeURIComponent(reference)}/reschedule`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            token,
            staffId: slot.staffId,
            startsAt: slot.startsAt,
          }),
        },
      );
      location.reload();
    } catch (e: any) {
      setError(e.message);
      setBusy(false);
    }
  }
  return (
    <main className="booking-shell">
      <section className="booking-card confirmation">
        <CalendarDays />
        <h1>Manage appointment</h1>
        <p>
          <b>{booking.service_name}</b> with {booking.staff_name}
        </p>
        <p>{new Date(booking.starts_at).toLocaleString("en-GB")}</p>
        <p>
          Reference: <b>{booking.public_reference}</b> · Status:{" "}
          {String(booking.status).replaceAll("_", " ")}
        </p>
        {booking.status !== "CANCELLED" ? (
          <>
            <div className="booking-grid">
              <label>
                Choose another date
                <input
                  type="date"
                  min={new Date().toISOString().slice(0, 10)}
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                />
              </label>
              <button onClick={find}>Find new times</button>
            </div>
            {slots.length ? (
              <div className="booking-slots">
                {slots.map((s: any) => (
                  <button
                    disabled={busy}
                    key={`${s.staffId}-${s.startsAt}`}
                    onClick={() => move(s)}
                  >
                    <Clock3 />
                    <b>
                      {new Date(s.startsAt).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </b>
                    <small>{s.staffName}</small>
                  </button>
                ))}
              </div>
            ) : null}
            <button className="secondary" onClick={onCancel}>
              Cancel appointment
            </button>
          </>
        ) : (
          <p>This appointment has been cancelled.</p>
        )}
        {error ? <p className="auth-error">{error}</p> : null}
      </section>
    </main>
  );
}
export function PublicBooking() {
  const params = new URLSearchParams(location.search),
    manageReference = params.get("reference"),
    manageToken = params.get("token");
  const [data, setData] = useState<any>(null),
    [managed, setManaged] = useState<any>(null),
    [serviceId, setService] = useState(""),
    [staffId, setStaff] = useState(""),
    [date, setDate] = useState(
      new Date(Date.now() + 86400000).toISOString().slice(0, 10),
    ),
    [slots, setSlots] = useState<any[]>([]),
    [slot, setSlot] = useState<any>(null),
    [customer, setCustomer] = useState({
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      notes: "",
    }),
    [confirmation, setConfirmation] = useState<any>(null),
    [error, setError] = useState("");
  useEffect(() => {
    if (manageReference && manageToken)
      call(
        `/api/public/appointments/manage/${encodeURIComponent(manageReference)}?token=${encodeURIComponent(manageToken)}`,
      )
        .then(setManaged)
        .catch((e) => setError(e.message));
    else
      call("/api/public/appointments/catalog")
        .then(setData)
        .catch((e) => setError(e.message));
  }, []);
  const staff = useMemo(
    () =>
      data?.staff.filter((s: any) =>
        data.assignments.some(
          (a: any) => a.staff_id === s.id && a.service_id === serviceId,
        ),
      ) || [],
    [data, serviceId],
  );
  async function find() {
    setError("");
    try {
      const q = new URLSearchParams({
        serviceId,
        date,
        ...(staffId ? { staffId } : {}),
      });
      setSlots((await call(`/api/public/appointments/slots?${q}`)).slots);
      setSlot(null);
    } catch (e: any) {
      setError(e.message);
    }
  }
  async function book(e: FormEvent) {
    e.preventDefault();
    try {
      const x = await call("/api/public/appointments/book", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          ...customer,
          serviceId,
          staffId: slot.staffId,
          startsAt: slot.startsAt,
          paymentChoice: "PAY_ON_DAY",
        }),
      });
      setConfirmation({ ...x, ...slot });
    } catch (e: any) {
      setError(e.message);
    }
  }
  async function cancel() {
    if (!confirm("Cancel this appointment?")) return;
    try {
      await call(
        `/api/public/appointments/manage/${encodeURIComponent(manageReference!)}/cancel`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ token: manageToken }),
        },
      );
      setManaged({ ...managed, status: "CANCELLED" });
    } catch (e: any) {
      setError(e.message);
    }
  }
  if (manageReference && manageToken) {
    if (error && !managed)
      return (
        <main className="booking-shell">
          <section className="booking-card">
            <p className="auth-error">{error}</p>
          </section>
        </main>
      );
    if (!managed)
      return (
        <main className="booking-shell">
          <p>Loading your appointment…</p>
        </main>
      );
    return (
      <ManageBooking
        reference={manageReference}
        token={manageToken}
        booking={managed}
        onCancel={cancel}
      />
    );
  }
  if (error && !data)
    return (
      <main className="booking-shell">
        <p className="auth-error">{error}</p>
      </main>
    );
  if (!data)
    return (
      <main className="booking-shell">
        <p>Loading online booking…</p>
      </main>
    );
  if (confirmation) {
    const manageUrl = `${location.origin}/book?reference=${encodeURIComponent(confirmation.reference)}&token=${encodeURIComponent(confirmation.manageToken)}`;
    return (
      <main className="booking-shell">
        <section className="booking-card confirmation">
          <CheckCircle2 />
          <h1>Appointment confirmed</h1>
          <p>
            Your reference is <b>{confirmation.reference}</b>.
          </p>
          <p>
            {new Date(confirmation.startsAt).toLocaleString("en-GB")} with{" "}
            {confirmation.staffName}
          </p>
          <p>
            <b>{money(confirmation.pricePence)}</b> to pay on the day
            {confirmation.discountPence
              ? ` — online booking saving ${money(confirmation.discountPence)}`
              : ""}
          </p>
          <a className="booking-manage-link" href={manageUrl}>
            Manage, reschedule or cancel this appointment
          </a>
          <small>Save the management link. It is private to you.</small>
        </section>
      </main>
    );
  }
  return (
    <main className="booking-shell">
      <header>
        <span>◆</span>
        <div>
          <b>{data.company}</b>
          <small>Online booking · Powered by TaskRoute</small>
        </div>
      </header>
      <section className="booking-card">
        <h1>Book an appointment</h1>
        <p>Choose a service, staff member and available time.</p>
        <div className="booking-grid">
          <label>
            Service
            <select
              value={serviceId}
              onChange={(e) => {
                setService(e.target.value);
                setStaff("");
                setSlots([]);
              }}
            >
              <option value="">Choose a service</option>
              {data.services.map((s: any) => (
                <option value={s.id} key={s.id}>
                  {s.name} — {money(s.price_pence)}
                </option>
              ))}
            </select>
          </label>
          <label>
            Staff
            <select
              value={staffId}
              onChange={(e) => setStaff(e.target.value)}
              disabled={!serviceId}
            >
              <option value="">Any available staff</option>
              {staff.map((s: any) => (
                <option value={s.id} key={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Date
            <input
              type="date"
              min={new Date().toISOString().slice(0, 10)}
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </label>
          <button disabled={!serviceId} onClick={find}>
            <CalendarDays />
            Find times
          </button>
        </div>
        {slots.length ? (
          <div className="booking-slots">
            {slots.map((s: any) => (
              <button
                className={
                  slot?.staffId === s.staffId && slot?.startsAt === s.startsAt
                    ? "selected"
                    : ""
                }
                key={`${s.staffId}-${s.startsAt}`}
                onClick={() => setSlot(s)}
              >
                <Clock3 />
                <b>
                  {new Date(s.startsAt).toLocaleTimeString("en-GB", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </b>
                <small>
                  {s.staffName} · {money(s.pricePence)}
                  {s.discountPence
                    ? ` · save ${money(s.discountPence)} online`
                    : ""}
                </small>
              </button>
            ))}
          </div>
        ) : null}
        {slot ? (
          <form className="booking-customer" onSubmit={book}>
            <h2>Your details</h2>
            <label>
              Name
              <input
                required
                value={customer.customerName}
                onChange={(e) =>
                  setCustomer({ ...customer, customerName: e.target.value })
                }
              />
            </label>
            <label>
              Email
              <input
                required
                type="email"
                value={customer.customerEmail}
                onChange={(e) =>
                  setCustomer({ ...customer, customerEmail: e.target.value })
                }
              />
            </label>
            <label>
              Phone
              <input
                value={customer.customerPhone}
                onChange={(e) =>
                  setCustomer({ ...customer, customerPhone: e.target.value })
                }
              />
            </label>
            <label className="wide">
              Booking notes
              <textarea
                value={customer.notes}
                onChange={(e) =>
                  setCustomer({ ...customer, notes: e.target.value })
                }
              />
            </label>
            <div className="wide booking-summary">
              <span>
                {new Date(slot.startsAt).toLocaleString("en-GB")} with{" "}
                {slot.staffName}
              </span>
              <strong>
                Pay on the day · {money(slot.pricePence)}
                {slot.discountPence ? (
                  <small>
                    Normally {money(slot.originalPricePence)} — online booking
                    saves {money(slot.discountPence)}
                  </small>
                ) : null}
              </strong>
            </div>
            <button className="wide">Confirm appointment</button>
          </form>
        ) : null}
        {error ? <p className="auth-error">{error}</p> : null}
      </section>
    </main>
  );
}
